/**
 * BaseForge-Vorschau mit den originalen Rust-Modellen der 3D-Map (building-models/).
 * Wird als eigenes Bundle (/tools/map-viewer/baseforge-viewer.js) auf der BaseForge-Seite
 * und im Admin geladen; Platzierung und Koordinatenumrechnung wie in base-layer.ts.
 */
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { loadOriginalModel, placeOriginalModelInstances, type OriginalModel } from '../scene/original-prefabs';
import { composeFoundationTemplate, selectedDeployableModelKey } from '../scene/base-layer';
import { adoptMaterials, roofPartKeys, roofParts } from '../scene/roof-parts';

export { roofParts };

/** Ein Element aus /api/bases/templates/:id/preview */
export interface PreviewElement {
  t: 'b' | 'd' | 's';
  n: string;
  f?: string;
  p: [number, number, number];
  q?: [number, number, number, number];
  y?: number;
  g?: string;
  k?: string;
  c?: number;
}

const GRADES: Record<string, string> = { twig: 'twig', wood: 'wood', stone: 'stone', metal: 'metal', hqm: 'toptier' };
const SKINS: Record<string, string> = {
  '2': 'gingerbread', '10220': 'adobe', '10221': 'container', '10223': 'brick',
  '10225': 'brutalist', '10232': 'frontier', '10326': 'jungle', '10430': 'spacestation', '10472': 'crypt',
};
/** Farben des Container-Skins in der Reihenfolge des Spiels (1–16), wie im Plugin. */
const CONTAINER_COLOURS = [
  0x8cbfeb, 0x99d98c, 0xb899e6, 0x8c1f1f, 0xeb8026, 0xededed, 0x1a1a1a, 0x734d2e,
  0x1f3385, 0x1f5c29, 0xeb6b6b, 0xdbc999, 0xf2d933, 0x4d4d4d, 0x1a7373, 0xb3b3b3,
];

/** Modell-Schlüssel eines Bauteils: Skin-Variante, sonst Baustufe (wie selectedBuildingModelKey). */
export function blockModelKey(e: PreviewElement, models: Record<string, unknown>): string | null {
  const name = e.n === 'block.stair.lshape' ? 'stairs.l' : e.n === 'block.stair.ushape' ? 'stairs.u' : e.n;
  const skin = e.k ? SKINS[e.k] : undefined;
  if (skin && models[`${name}.${skin}`]) return `${name}.${skin}`;
  const grade = `${name}.${GRADES[e.g ?? 'twig'] ?? 'twig'}`;
  if (models[grade]) return grade;
  // Skins ohne eigenes Modell (z. B. Dreiecksdächer) → gleiche Form in Stein
  return models[`${name}.stone`] ? `${name}.stone` : null;
}

export function modelKeyOf(e: PreviewElement, models: Record<string, OriginalModel>): string | null {
  if (e.t === 'b') return blockModelKey(e, models);
  if (!e.f) return null;
  const key = selectedDeployableModelKey(e.f, models);
  return models[key] ? key : null;
}

/** Etage eines Elements im 3-m-Raster (Böden/Decken zählen zur Etage darüber). */
export function levelOf(e: PreviewElement): number {
  return Math.max(0, Math.round(e.p[1] / 3 + (e.t === 'b' ? 0 : -0.3)));
}

/** Unity-Quaternion → Euler-Winkel in Grad (Reihenfolge YXZ wie placeOriginalModelInstances). */
export function eulerDegrees(q: [number, number, number, number] | undefined, yaw = 0) {
  const euler = new THREE.Euler(0, 0, 0, 'YXZ');
  if (q) euler.setFromQuaternion(new THREE.Quaternion(q[0], q[1], q[2], q[3]).normalize(), 'YXZ');
  else euler.set(0, THREE.MathUtils.degToRad(yaw), 0, 'YXZ');
  return { x: THREE.MathUtils.radToDeg(euler.x), y: THREE.MathUtils.radToDeg(euler.y), z: THREE.MathUtils.radToDeg(euler.z) };
}

// ------------------------------------------------------------------ Modelle (einmal pro Seite)

let indexPromise: Promise<Record<string, OriginalModel>> | null = null;
function modelIndex(): Promise<Record<string, OriginalModel>> {
  indexPromise ??= fetch(`${import.meta.env.BASE_URL}building-models/index.json`)
    .then(r => { if (!r.ok) throw new Error('building models unavailable'); return r.json(); })
    .then(index => index.models as Record<string, OriginalModel>)
    .catch(err => { indexPromise = null; throw err; });
  return indexPromise;
}

const templates = new Map<string, Promise<THREE.Group | null>>();
function template(key: string, models: Record<string, OriginalModel>): Promise<THREE.Group | null> {
  let hit = templates.get(key);
  if (!hit) {
    hit = (async () => {
      const model = models[key];
      if (!model) return null;
      let group = await loadOriginalModel(model);
      await adoptMaterials(group, key, model, models, k => template(k, models));
      // Fundamente: Seitenwände (Sockel) wie in der 3D-Map dazusetzen
      const triangle = key.startsWith('foundation.triangle.');
      const square = !triangle && /^foundation\.[a-z]+$/.test(key);
      const suffix = triangle ? key.slice('foundation.triangle.'.length) : square ? key.slice('foundation.'.length) : '';
      const wall = suffix ? models[`foundation.wall.${suffix}`] : undefined;
      if (wall) group = composeFoundationTemplate(group, await loadOriginalModel(wall), triangle);
      return group;
    })().catch(() => null);
    templates.set(key, hit);
  }
  return hit;
}

/** Container-Skin: Materialien je Farbe einmal einfärben. */
const tinted = new Map<string, THREE.Group>();
function tintedTemplate(key: string, base: THREE.Group, colour: number): THREE.Group {
  const id = `${key}#${colour}`;
  let group = tinted.get(id);
  if (!group) {
    group = base.clone(true);
    const color = new THREE.Color(CONTAINER_COLOURS[colour - 1]);
    const cache = new Map<THREE.Material, THREE.Material>();
    group.traverse(obj => {
      if (!(obj instanceof THREE.Mesh)) return;
      const tint = (m: THREE.Material) => {
        if (!cache.has(m)) {
          const copy = m.clone() as THREE.MeshStandardMaterial;
          if (copy.color) copy.color.copy(color).lerp(new THREE.Color(0xffffff), 0.25);
          cache.set(m, copy);
        }
        return cache.get(m)!;
      };
      obj.material = Array.isArray(obj.material) ? obj.material.map(tint) : tint(obj.material);
    });
    tinted.set(id, group);
  }
  return group;
}

// ------------------------------------------------------------------ Szene

interface BuiltBase {
  root: THREE.Group;
  levels: number[];
  box: THREE.Box3;
  ground: number;
  missing: number;
}

/**
 * Wurzel wie baseRoot in der 3D-Map: Rust/Unity ist linkshändig, three.js rechtshändig → X spiegeln.
 * placeOriginalModelInstances rechnet mit dieser Spiegelung; ohne sie ist die Base seitenverkehrt.
 */
export function createBaseRoot(): THREE.Group {
  const root = new THREE.Group();
  root.scale.x = -1;
  return root;
}

async function buildBase(elements: PreviewElement[]): Promise<BuiltBase> {
  const models = await modelIndex();
  const groups = new Map<string, { key: string; colour: number; level: number; items: PreviewElement[] }>();
  const fallback: PreviewElement[] = [];
  const roofs = roofParts(elements);
  const add = (key: string, e: PreviewElement, colour: number) => {
    const level = levelOf(e);
    const id = `${key}|${colour}|${level}`;
    const g = groups.get(id) ?? { key, colour, level, items: [] };
    g.items.push(e);
    groups.set(id, g);
  };
  elements.forEach((e, i) => {
    const key = modelKeyOf(e, models);
    if (!key) { if (e.t !== 's') fallback.push(e); return; }
    const colour = e.k === '10221' && key.endsWith('.container') && e.c ? e.c : 0;
    const parts = roofs.get(i);
    // Dächer aus ihren Teilen zusammensetzen, sofern die Varianten dieses Materials exportiert sind
    const partKeys = parts ? roofPartKeys(key, parts, models) : [];
    if (partKeys.length) { for (const k of partKeys) add(k, e, colour); return; }
    add(key, e, colour);
  });
  const root = createBaseRoot();
  let missing = 0;
  await Promise.all([...groups.values()].map(async g => {
    const base = await template(g.key, models);
    if (!base) { missing += g.items.length; fallback.push(...g.items.filter(e => e.t !== 's')); return; }
    const tpl = g.colour ? tintedTemplate(g.key, base, g.colour) : base;
    const placed = placeOriginalModelInstances(tpl, g.items.map(e => ({
      category: 'baseforge', id: 0,
      position: { x: e.p[0], y: e.p[1], z: e.p[2] },
      rotation: eulerDegrees(e.q, e.y),
      scale: { x: 1, y: 1, z: 1 },
    })));
    placed.userData.level = g.level;
    root.add(placed);
  }));
  // Ohne Modell: schlichte Kästen, damit nichts fehlt
  if (fallback.length) {
    const geo = new THREE.BoxGeometry(1, 1, 1);
    const mat = new THREE.MeshStandardMaterial({ color: 0xee7e2c, roughness: 0.8 });
    const byLevel = new Map<number, PreviewElement[]>();
    for (const e of fallback) { const l = levelOf(e); byLevel.set(l, [...(byLevel.get(l) ?? []), e]); }
    for (const [level, list] of byLevel) {
      const mesh = new THREE.InstancedMesh(geo, mat, list.length);
      const m = new THREE.Matrix4();
      list.forEach((e, i) => {
        const size = e.t === 'b' ? 3 : 0.8;
        m.compose(new THREE.Vector3(e.p[0], e.p[1] + size / 2, e.p[2]), new THREE.Quaternion(), new THREE.Vector3(size, e.t === 'b' ? 0.2 : size, size));
        mesh.setMatrixAt(i, m);
      });
      const holder = new THREE.Group();
      holder.userData.level = level;
      holder.add(mesh);
      root.add(holder);
    }
  }
  root.updateMatrixWorld(true);
  const levels = [...new Set(elements.filter(e => e.t === 'b').map(levelOf))].sort((a, b) => a - b);
  const box = new THREE.Box3().setFromObject(root);
  if (box.isEmpty()) box.set(new THREE.Vector3(-3, 0, -3), new THREE.Vector3(3, 3, 3));
  // Boden 1 m unter den Fundamenten: die 3 m hohen Sockel stecken wie im Spiel im Gelände
  const foundations = elements.filter(e => e.t === 'b' && e.n.startsWith('foundation'));
  const ground = (foundations.length ? Math.min(...foundations.map(e => e.p[1])) : box.min.y + 1) - 1;
  box.min.y = Math.max(box.min.y, ground);
  root.add(groundPlane(box, ground));
  return { root, levels, box, ground, missing };
}

function groundPlane(box: THREE.Box3, y: number): THREE.Object3D {
  const size = box.getSize(new THREE.Vector3());
  const radius = Math.max(size.x, size.z) * 0.9 + 6;
  const group = new THREE.Group();
  const disc = new THREE.Mesh(
    new THREE.CircleGeometry(radius, 64).rotateX(-Math.PI / 2),
    new THREE.MeshStandardMaterial({ color: 0x2a2219, roughness: 1, transparent: true, opacity: 0.92 }),
  );
  const grid = new THREE.GridHelper(radius * 2, Math.round(radius * 2 / 3), 0x5a4330, 0x3a2d22);
  (grid.material as THREE.Material).transparent = true;
  (grid.material as THREE.Material).opacity = 0.45;
  grid.position.y = 0.01;
  group.add(disc, grid);
  const center = box.getCenter(new THREE.Vector3());
  group.position.set(-center.x, y, center.z); // lokale Koordinaten der gespiegelten Wurzel
  group.userData.level = -1; // bleibt bei jeder Etagen-Auswahl sichtbar
  return group;
}

function lights(scene: THREE.Scene) {
  scene.add(new THREE.HemisphereLight(0xfff4e6, 0x3b2a1e, 1.6));
  const sun = new THREE.DirectionalLight(0xffffff, 2.4);
  sun.position.set(-40, 70, 30);
  scene.add(sun);
  const fill = new THREE.DirectionalLight(0xffd2a8, 0.6);
  fill.position.set(50, 20, -40);
  scene.add(fill);
}

/**
 * Kamera schräg von oben auf die Base richten und so weit zurücksetzen, dass alle acht Ecken
 * der Bounding-Box ins Bild passen (horizontal und vertikal), mit etwas Rand.
 */
export function frame(camera: THREE.PerspectiveCamera, box: THREE.Box3, angleDeg: number, zoom = 1) {
  const center = box.getCenter(new THREE.Vector3());
  const a = THREE.MathUtils.degToRad(angleDeg);
  const dir = new THREE.Vector3(Math.cos(a), 0.78, Math.sin(a)).normalize();
  const right = new THREE.Vector3().crossVectors(dir.clone().negate(), new THREE.Vector3(0, 1, 0)).normalize();
  const up = new THREE.Vector3().crossVectors(right, dir.clone().negate());
  const tanV = Math.tan(THREE.MathUtils.degToRad(camera.fov / 2));
  const tanH = tanV * camera.aspect;
  let dist = 4;
  for (const x of [box.min.x, box.max.x]) for (const y of [box.min.y, box.max.y]) for (const z of [box.min.z, box.max.z]) {
    const v = new THREE.Vector3(x, y, z).sub(center);
    const depth = v.dot(dir);
    dist = Math.max(dist, depth + Math.abs(v.dot(up)) / tanV, depth + Math.abs(v.dot(right)) / tanH);
  }
  dist = dist * 1.08 / zoom;
  camera.position.copy(center).addScaledVector(dir, dist);
  camera.near = Math.max(0.1, dist / 100);
  camera.far = dist * 10;
  camera.lookAt(center);
  camera.updateProjectionMatrix();
  return center;
}

export function supported(): boolean {
  try {
    const c = document.createElement('canvas');
    return !!(c.getContext('webgl2') || c.getContext('webgl'));
  } catch { return false; }
}

// ------------------------------------------------------------------ Interaktive Ansicht

export interface BfView {
  levels: number[];
  missing: number;
  ready: Promise<void>;
  setLevel(index: number | null): void;
  rotate(delta: number): void;
  resetView(): void;
  redraw(): void;
  destroy(): void;
}

export function create(canvas: HTMLCanvasElement, elements: PreviewElement[], opts: { interactive?: boolean } = {}) {
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;
  const scene = new THREE.Scene();
  lights(scene);
  const camera = new THREE.PerspectiveCamera(32, 1, 0.1, 2000);
  const controls = new OrbitControls(camera, canvas);
  controls.enabled = !!opts.interactive;
  controls.enableDamping = true;
  controls.dampingFactor = 0.12;
  controls.maxPolarAngle = Math.PI * 0.495;
  controls.screenSpacePanning = true;

  let built: BuiltBase | null = null;
  let angle = 45;
  let destroyed = false;
  let frameId = 0;
  const render = () => {
    frameId = 0;
    if (destroyed) return;
    const moving = controls.update();
    renderer.render(scene, camera);
    if (moving) frameId = requestAnimationFrame(render);
  };
  const request = () => { if (!frameId && !destroyed) frameId = requestAnimationFrame(render); };
  controls.addEventListener('change', request);

  const resize = () => {
    const w = canvas.clientWidth || 300, h = canvas.clientHeight || 200;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    request();
  };
  const observer = new ResizeObserver(resize);
  observer.observe(canvas);
  resize();

  const reset = () => {
    if (!built) return;
    controls.target.copy(frame(camera, built.box, angle));
    controls.update();
    request();
  };

  const view: BfView = {
    levels: [],
    missing: 0,
    ready: Promise.resolve(),
    setLevel(index: number | null) {
      if (!built) return;
      const max = index == null || index >= built.levels.length - 1 ? Infinity : built.levels[index];
      for (const child of built.root.children) child.visible = (child.userData.level ?? 0) <= max;
      request();
    },
    rotate(delta: number) {
      // Kamera um die senkrechte Achse durch den Mittelpunkt drehen
      const offset = camera.position.clone().sub(controls.target);
      offset.applyAxisAngle(new THREE.Vector3(0, 1, 0), THREE.MathUtils.degToRad(-delta));
      camera.position.copy(controls.target).add(offset);
      camera.lookAt(controls.target);
      request();
    },
    resetView() { angle = 45; reset(); },
    redraw: request,
    destroy() {
      destroyed = true;
      if (frameId) cancelAnimationFrame(frameId);
      observer.disconnect();
      controls.dispose();
      renderer.dispose();
      renderer.forceContextLoss();
    },
  };
  view.ready = buildBase(elements).then(result => {
    if (destroyed) return;
    built = result;
    view.levels = result.levels;
    view.missing = result.missing;
    scene.add(result.root);
    resize();
    reset();
  });
  return view;
}

// ------------------------------------------------------------------ Vorschaubilder

// Ein gemeinsamer WebGL-Kontext für alle Karten (Browser erlauben nur wenige gleichzeitig)
let shared: { renderer: THREE.WebGLRenderer; camera: THREE.PerspectiveCamera } | null = null;
let queue: Promise<unknown> = Promise.resolve();

export function thumbnail(target: HTMLCanvasElement, elements: PreviewElement[]): Promise<boolean> {
  const job = queue.then(async () => {
    const built = await buildBase(elements);
    if (!target.isConnected) return false;
    if (!shared) {
      const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1.05;
      shared = { renderer, camera: new THREE.PerspectiveCamera(32, 1, 0.1, 2000) };
    }
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = Math.round((target.clientWidth || 320) * dpr), h = Math.round((target.clientHeight || 170) * dpr);
    shared.renderer.setSize(w, h, false);
    shared.camera.aspect = w / h;
    const scene = new THREE.Scene();
    lights(scene);
    scene.add(built.root);
    frame(shared.camera, built.box, 45);
    shared.renderer.setClearColor(0x000000, 0);
    shared.renderer.render(scene, shared.camera);
    target.width = w;
    target.height = h;
    const ctx = target.getContext('2d');
    ctx?.clearRect(0, 0, w, h);
    ctx?.drawImage(shared.renderer.domElement, 0, 0);
    return true;
  });
  queue = job.catch(() => false);
  return job;
}

/**
 * Bild einer Vorlage als PNG-Data-URL (für die serverseitigen Vorschaubilder im Spiel:
 * services/baseforgeThumbs.js öffnet baseforge-render.html im Headless-Browser).
 */
export async function renderImage(elements: PreviewElement[], width = 640, height = 360): Promise<string> {
  const built = await buildBase(elements);
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
  try {
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;
    renderer.setPixelRatio(1);
    renderer.setSize(width, height, false);
    renderer.setClearColor(0x000000, 0);
    const camera = new THREE.PerspectiveCamera(32, width / height, 0.1, 2000);
    const scene = new THREE.Scene();
    lights(scene);
    // Spielbild ohne Boden: transparenter Hintergrund, Fundament-Sockel auf Bodenhöhe abschneiden
    for (const child of built.root.children) if (child.userData.level === -1) child.visible = false;
    renderer.clippingPlanes = [new THREE.Plane(new THREE.Vector3(0, 1, 0), -built.ground)];
    scene.add(built.root);
    frame(camera, built.box, 45);
    renderer.render(scene, camera);
    return renderer.domElement.toDataURL('image/png');
  } finally {
    renderer.dispose();
    renderer.forceContextLoss();
  }
}
